import { FormPageHeader, Paginator } from 'components/Shared';
import { useAuth, usePaginationCache } from 'hooks';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { useLocation } from 'react-router-dom';
import { ALL_ORDER_STATUS } from 'constants/Checkboxes';
import {
  ExportOrdersActions,
  PaginateOrdersActions as PaginateActions,
} from 'store/ducks/orders';
import Filters, { FindOrders } from './Filters/index';
import OrdersList from './OrdersList';
import OrderModal, { Ref as OrderModalRef } from '../OrderModal';
import * as S from './styles';
import {
  formatOrderActionAuthor,
  getLatestOrderActionLogByType,
  Order,
  orderActionTypeByTimestampField,
} from 'contracts/Orders';
import { Formatter } from 'utils';
import { DownloadXLS } from 'components/Shared/DownloadXLS';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { exportXLSX } from 'utils';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const PAGE_SIZE_STORAGE_KEY = 'managerial-panel-page-size';

const getStoredPageSize = (): number => {
  try {
    const pageSize = Number(window.sessionStorage.getItem(PAGE_SIZE_STORAGE_KEY));
    return PAGE_SIZE_OPTIONS.includes(pageSize) ? pageSize : 10;
  } catch {
    return 10;
  }
};

export const PaginatedOrders: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const location = useLocation();
  const { userBelongsToAnyOf } = useAuth();
  const orderModalRef = useRef<OrderModalRef>(null);

  const {
    data: orders,
    loading: loadingOrders,
    pagination,
  } = useSelector((state: RootState) => state.paginateOrders);

  const { loading: loadingExportOrders } = useSelector(
    (state: RootState) => state.exportOrders
  );

  //monta colunas
  const generateXLSX = useCallback((ordersToExport: Order[]) => {

    if (ordersToExport.length) {
      const fileName = 'AGENDAMENTO-PAINEL-GERENCIAL';
      const data = ordersToExport.map((item: Order) => {
        const {
          id,
          scheduledAt,
          dock,
          vehicleType,
          vehiclePlate,
          company,
          carrier,
          status,
          orderType,
          operationType,
          cargoType,
          warehouse,
          weightCapacity,
          duration,
          driverName,
          driverDocument,
          devolutionNumber,
          exportationSecuritySeal,
          exportationContainer,
          importationOrder,
          importationSequential,
          importationDeclaration,
          observation,
          material,
          vehicleArrivedAt,
          vehicleDepartedAt,
          startedAt,
          finishedAt,
          noShowAt,
          canceledAt,
          createdByUser,
          createdAt,
          orderItems,
          actionLogs,
        } = item;

        const latestActionLogByType = getLatestOrderActionLogByType(actionLogs);


        return {
          'Coleta': id ? id : '',
          'Status': status ? status : '',
          'Data do Agendamento': scheduledAt
            ? Formatter.date(scheduledAt, { format: 'dd/MM/yy HH:mm' })
            : '',
          'Tipo de Ordem': orderType.name ? orderType.name : '',
          'Tipo de Operacao': operationType.name ? operationType.name : '',
          'Tipo de Veiculo': vehicleType.name ? vehicleType.name : '',
          'Carga': cargoType.name ? cargoType.name : '',
          'Cliente': company.tradeName ? company.tradeName : '',
          'Transportadora': carrier.tradeName ? carrier.tradeName : '',
          'Armazem': warehouse.tradeName ? warehouse.tradeName : '',
          'Doca': dock.name ? dock.name : '',
          'Capacidade': weightCapacity ? weightCapacity : '',
          'Duracao': duration ? duration : '',
          'Placa': vehiclePlate ? vehiclePlate : '',
          'Documento do  motorista': driverDocument ? driverDocument : '',
          'Nome do Motorista': driverName ? driverName : '',
          'Documento': orderItems
            .map(({ document }) => document || '')
            .join('\n'),
          'Peso': orderItems.map(({ weight }) => weight ?? '').join('\n'),
          'Valor': orderItems.map(({ value }) => value ?? '').join('\n'),
          'Numero_devolucao': devolutionNumber ? devolutionNumber : '',
          'Exportacao_Lacre': exportationSecuritySeal
            ? exportationSecuritySeal
            : '',
          'Exportacao_Container': exportationContainer
            ? exportationContainer
            : '',
          'Ordem_importacao': importationOrder ? importationOrder : '',
          'Ordem_Sequencial': importationSequential
            ? importationSequential
            : '',
          'Importacao_Sequencial': importationSequential
            ? importationSequential
            : '',
          'Observacao': observation ? observation : '',
          'Produto': material ? material : '',
          'Data/Hora Chegada Veiculo': vehicleArrivedAt
            ? Formatter.date(vehicleArrivedAt, { format: 'dd/MM/yy HH:mm' })
            : '',
          'Registrado por - Chegada Veículo': formatOrderActionAuthor(
            latestActionLogByType[orderActionTypeByTimestampField.vehicleArrivedAt]
          ),
          'Data/Hora Saida Veiculo': vehicleDepartedAt
            ? Formatter.date(vehicleDepartedAt, { format: 'dd/MM/yy HH:mm' })
            : '',
          'Registrado por - Saida Veículo': formatOrderActionAuthor(
            latestActionLogByType[orderActionTypeByTimestampField.vehicleDepartedAt]
          ),
          'Data/Hora Inicio': startedAt
            ? Formatter.date(startedAt, { format: 'dd/MM/yy HH:mm' })
            : '',
          'Registrado por - Inicio': formatOrderActionAuthor(
            latestActionLogByType[orderActionTypeByTimestampField.startedAt]
          ),
          'Data/Hora Fim': finishedAt
            ? Formatter.date(finishedAt, { format: 'dd/MM/yy HH:mm' })
            : '',
          'Registrado por - Fim': formatOrderActionAuthor(
            latestActionLogByType[orderActionTypeByTimestampField.finishedAt]
          ),
          'No Show': noShowAt
            ? Formatter.date(noShowAt, { format: 'dd/MM/yy HH:mm' })
            : '',
          'Registrado por - No show': formatOrderActionAuthor(
            latestActionLogByType[orderActionTypeByTimestampField.noShowAt]
          ),
          'Criado por': createdByUser.name ? createdByUser.name : '',
          'Data/Hora Criacao': createdAt
            ? Formatter.date(createdAt, { format: 'dd/MM/yy HH:mm' })
            : '',
          'Importacao_Declaracao': importationDeclaration
            ? importationDeclaration
            : '',
          ' Data/Hora Cancelamento': canceledAt
            ? Formatter.date(canceledAt, { format: 'dd/MM/yy HH:mm' })
            : '',
          'Registrado por - Cancelamento': formatOrderActionAuthor(
            latestActionLogByType[orderActionTypeByTimestampField.canceledAt]
          ),
        };
      });

      exportXLSX(data, fileName);
    }
  }, []);

  const { paginationCache, updatePaginationCache, handleFilter, handleSort } =
    usePaginationCache<FindOrders>('orders');

  const [query, setQuery] = useState<FindOrders>(() => {
    const cachedQuery = { ...(paginationCache ?? {}) } as FindOrders;
    const legacyWarehouseId = cachedQuery.warehouseId;
    delete cachedQuery.warehouseId;

    return {
      page: 1,
      limit: getStoredPageSize(),
      ...cachedQuery,
      status: cachedQuery.status?.includes('todos')
        ? (ALL_ORDER_STATUS as FindOrders['status'])
        : cachedQuery.status ?? (ALL_ORDER_STATUS as FindOrders['status']),
      warehouseIds:
        cachedQuery.warehouseIds ??
        (legacyWarehouseId ? [Number(legacyWarehouseId)] : []),
    };
  });

  const exportOrders = useCallback(() => {
    dispatch(ExportOrdersActions.request(query, generateXLSX));
  }, [query, dispatch, generateXLSX]);

  const onPageChange = useCallback((page: number): void => {
    setQuery((state: FindOrders) => ({ ...state, page }));
  }, []);

  const onQueryChange = useCallback((): void => {
    dispatch(PaginateActions.request(query));
  }, [dispatch, query]);

  const CreateOrderButton = useCallback((): JSX.Element => {
    if (
      !userBelongsToAnyOf(
        'admin',
        'carrierMember',
        'warehouseMember',
        'companyMember'
      )
    ) {
      return <></>;
    }

    return (
      <S.LinkButton
        size="small"
        to="/agendamento/criar"
        state={{ from: location.pathname }}
      >
        <S.PlusIcon /> Novo agendamento
      </S.LinkButton>
    );
  }, [location.pathname, userBelongsToAnyOf]);

  const CreateOrderButtonDownloand = useCallback((): JSX.Element => {
    if (
      !userBelongsToAnyOf(
        'admin',
        'carrierMember',
        'warehouseMember',
        'companyMember'
      )
    ) {
      return <></>;
    }

    return (
      <DownloadXLS loading={loadingExportOrders} onExport={exportOrders} /> //Botão
    );
  }, [userBelongsToAnyOf, loadingExportOrders, exportOrders]);

  useEffect(() => {
    onQueryChange();
  }, [onQueryChange]);

  useEffect(() => {
    updatePaginationCache({ ...query }, ['warehouseId']);
  }, [query, updatePaginationCache]);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(query.limit));
    } catch {
      // Storage can be unavailable in private browsing contexts.
    }
  }, [query.limit]);

  useEffect(
    () => () => {
      dispatch(PaginateActions.reset());
    },
    [dispatch]
  );

  return (
    <S.Container>
      <S.Panel>
        <OrderModal onUpdate={onQueryChange} ref={orderModalRef} />
        <FormPageHeader
          title="Painel gerencial"
          icon={<S.ListIcon />}
          isLoading={loadingOrders}
          actions={<CreateOrderButton />}
          downloand={<CreateOrderButtonDownloand />}
        />
        <Filters
          currentFilter={query}
          onFilter={(filter: FindOrders) =>
            handleFilter(query, filter, setQuery)
          }
        />
        <OrdersList
          orders={orders}
          currentSort={query}
          onSort={(sort) => handleSort(query, sort, setQuery)}
          onSelectOrder={(id) => orderModalRef.current?.selectOrder(id)}
        />
        <Paginator
          alwaysVisible
          onPageChange={onPageChange}
          pagination={pagination}
          trailingContent={
            <Select
              value={`${query.limit}`}
              onValueChange={(value) =>
                setQuery((current) => ({
                  ...current,
                  page: 1,
                  limit: Number(value),
                }))
              }
            >
              <SelectTrigger
                aria-label="Resultados por página"
                className="ml-3 h-8 w-[72px] rounded-lg bg-white px-2 font-GilroyRegular text-xs text-slate-700 hover:bg-gray-50 focus:ring-1 focus:ring-[#EE7C2C] focus:ring-offset-0"
                style={{
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  boxShadow: 'none',
                }}
              >
                <SelectValue placeholder={query.limit} />
              </SelectTrigger>
              <SelectContent
                side="top"
                className="min-w-[72px] border-slate-200 bg-white font-GilroyRegular"
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <SelectItem
                    key={option}
                    value={`${option}`}
                    className="cursor-pointer pl-7 text-xs text-slate-700 focus:bg-orange-50 focus:text-slate-900 data-[state=checked]:bg-orange-50 data-[state=checked]:font-semibold"
                  >
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
        />
      </S.Panel>
    </S.Container>
  );
};
